// The card that opens when you tap something ON the map — a parking lot, a bike
// rack — as opposed to something you looked up, which gets the Nearby sheet.
//
// One component for both, because they are the same object seen twice: a
// coloured mark you tapped, its name, what it is doing right now, a short list
// of facts, and at most one thing to do about it. Two components with this
// chrome copied would drift the moment either grew a row, and the whole point
// of a tap-anything card is that tapping anything feels the same.
//
// Layout rules live in the stylesheet (`.pfa-infocard`), and the load-bearing
// one is that only `facts` scrolls: the name, the badges and the action stay
// put, so a card squeezed by a short screen loses rows off a list rather than
// losing its button off the bottom.
import { Fragment, useEffect } from 'react';

/**
 * @param color   the mark's own colour — the swatch that ties card to map
 * @param title   what the thing is called
 * @param status  `{ text, tone }` — what it is doing now, or null
 * @param note    a second, quieter badge (the price, the kind of rack)
 * @param facts   `[[label, value]]` — rendered as a <dl>, omitted when empty
 * @param action  `{ label, href }` or `{ label, onClick }`, or null
 */
export default function MapInfoCard({ color, title, status, note, facts = [], action, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows = facts.filter(([, v]) => v);

  return (
    <div className="pfa-panel pfa-infocard" role="dialog" aria-label={title}>
      <div className="pfa-infocard-head">
        {/* The colour of the mark that was tapped, so the card and the thing
            under it are obviously one object — this map tells its categories
            apart by colour alone. */}
        <span className="pfa-infocard-swatch" style={{ background: color }} />
        <strong>{title}</strong>
        <button className="pfa-places-x" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {(status?.text || note) && (
        <div className="pfa-infocard-now">
          {status?.text && <span className={`pfa-lot-badge ${status.tone || ''}`}>{status.text}</span>}
          {note && <span className="pfa-lot-rate">{note}</span>}
        </div>
      )}

      {/* Left out entirely when there is nothing in it: the list holds a
          one-row minimum open, and a blank gap reads as a failure to load. */}
      {!!rows.length && (
        <dl className="pfa-infocard-facts">
          {rows.map(([label, value]) => (
            <Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </Fragment>
          ))}
        </dl>
      )}

      {action && (action.href
        ? <a className="pfa-place-go" href={action.href} target="_blank" rel="noopener noreferrer">{action.label}</a>
        : <button className="pfa-place-go" onClick={action.onClick}>{action.label}</button>)}
    </div>
  );
}
